import type {
  BallotDTO,
  CreatePollInput,
  PollAnswer,
  PollDTO,
  PollListEntry,
  ScheduleDTO,
  UpdatePollInput,
  ScreenDTO,
  ScreenSettingsDTO,
  ViewRect,
  FileEntry,
  MapDTO,
  MapGrid,
  FogState,
  MapPinDTO,
  MapTokenDTO,
  MapTemplate,
  CatalogImportResult,
  CatalogItemDTO,
  CatalogKind,
  ItemSuggestion,
  AuthState,
  CreatedInviteDTO,
  InviteDTO,
  InviteInfoDTO,
  MemberDTO,
  UserDTO,
  DiscoveryDTO,
  NoteAccessDTO,
  CampaignDTO,
  CombatActionResult,
  CombatantDTO,
  CombatDTO,
  CombatListEntry,
  CreateNoteInput,
  GraphDTO,
  HierarchyDTO,
  ImportPreview,
  ImportReport,
  NoteDetail,
  NoteIndexEntry,
  NoteTypeDTO,
  NoteWithProps,
  OpenLink,
  SearchHit,
  UpdateNoteInput,
} from '@kartothek/shared';

import { ApiError } from './apiError';
import { t } from './i18n';
import { isCacheable, readCached, storeWriteResponse, writeCached } from './offline/cache';
import { canQueue, enqueue, newKey } from './offline/outbox';
import { getOfflineState, isUnreachableStatus, reportOffline, reportOnline } from './offline/state';
import { checkServerBuild, isOutdated } from './version';

export { ApiError };

/** Hängt die Verbindung (WLAN weg), nicht ewig warten – lieber auf die lokale Kopie ausweichen. */
const GET_TIMEOUT_MS = 10_000;
const WRITE_TIMEOUT_MS = 20_000;

function timeout(ms: number): AbortSignal | undefined {
  return typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(ms) : undefined;
}

async function errorFrom(res: Response): Promise<ApiError> {
  let data: { error?: string; message?: string } = {};
  try {
    data = await res.json();
  } catch {
    /* kein JSON */
  }
  return new ApiError(res.status, data.error ?? 'error', data.message ?? res.statusText);
}

async function getJson<T>(url: string): Promise<T> {
  const cacheable = isCacheable(url);
  const { online, pending } = getOfflineState();
  // Offline oder mit gepufferten Änderungen zeigt die lokale Kopie den aktuelleren Stand
  if (cacheable && (!online || pending > 0)) {
    const local = await readCached<T>(url);
    if (local !== undefined) return local;
  }
  let res: Response | null = null;
  try {
    res = await fetch(url, { headers: { 'X-Requested-With': 'kartothek' }, credentials: 'same-origin', signal: timeout(GET_TIMEOUT_MS) });
  } catch {
    /* Netzwerkfehler */
  }
  if (!res || isUnreachableStatus(res.status)) {
    reportOffline();
    const local = cacheable ? await readCached<T>(url) : undefined;
    if (local !== undefined) return local;
    throw new ApiError(0, 'offline', t('offline.notCached'));
  }
  reportOnline();
  checkServerBuild(res);
  if (!res.ok) throw await errorFrom(res);
  const data = (res.status === 204 ? undefined : await res.json()) as T;
  if (cacheable && data !== undefined) void writeCached(url, data);
  return data;
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  if (method === 'GET') return getJson<T>(url);
  // Veralteter Tab: nicht mehr schreiben, bis neu geladen wurde
  if (isOutdated()) {
    throw new ApiError(409, 'outdated', 'Es gibt eine neue Version von Kartothek. Bitte die Seite neu laden (F5).');
  }
  const form = body instanceof FormData;
  const queueable = !form && canQueue(method, url);
  const { online, pending } = getOfflineState();
  // Offline bzw. solange Änderungen gepuffert sind: lokal ausführen und hinten anstellen
  if (queueable && (!online || pending > 0)) return (await enqueue(method, url, body)) as T;

  const headers: Record<string, string> = { 'X-Requested-With': 'kartothek' };
  // Schlüssel auch online: Bricht die Verbindung mitten in der Anfrage ab, wird sie gepuffert und wirkt trotzdem nur einmal
  const key = queueable ? newKey() : undefined;
  if (key) headers['Idempotency-Key'] = key;
  let payload: BodyInit | undefined;
  if (form) payload = body;
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  let res: Response | null = null;
  try {
    res = await fetch(url, { method, headers, body: payload, credentials: 'same-origin', signal: queueable ? timeout(WRITE_TIMEOUT_MS) : undefined });
  } catch {
    /* Netzwerkfehler */
  }
  if (!res || isUnreachableStatus(res.status)) {
    reportOffline();
    if (queueable) return (await enqueue(method, url, body, key)) as T;
    throw new ApiError(0, 'offline', t('offline.unsupported'));
  }
  reportOnline();
  checkServerBuild(res);
  if (!res.ok) throw await errorFrom(res);
  const data = (res.status === 204 ? undefined : await res.json()) as T;
  if (!form) void storeWriteResponse(url, data);
  return data;
}

const c = (cid: string) => `/api/campaigns/${cid}`;

export const api = {
  authState: () => request<AuthState>('GET', '/api/auth/state'),
  setup: (name: string, password: string) => request<{ user: AuthState['user'] }>('POST', '/api/auth/setup', { name, password }),
  login: (name: string, password: string) => request<{ user: AuthState['user'] }>('POST', '/api/auth/login', { name, password }),
  logout: () => request<{ ok: true }>('POST', '/api/auth/logout'),
  changePassword: (current: string, next: string) => request<{ ok: true }>('POST', '/api/auth/password', { current, next }),

  members: (cid: string) => request<MemberDTO[]>('GET', `${c(cid)}/members`),
  updateMember: (cid: string, uid: string, role: MemberDTO['role']) => request<MemberDTO[]>('PATCH', `${c(cid)}/members/${uid}`, { role }),
  removeMember: (cid: string, uid: string) => request<{ ok: true }>('DELETE', `${c(cid)}/members/${uid}`),
  invites: (cid: string) => request<InviteDTO[]>('GET', `${c(cid)}/invites`),
  createInvite: (cid: string, input: { role: 'player' | 'co-dm'; label: string; maxUses: number | null; expiresInDays: number }) =>
    request<CreatedInviteDTO>('POST', `${c(cid)}/invites`, input),
  deleteInvite: (cid: string, id: string) => request<{ ok: true }>('DELETE', `${c(cid)}/invites/${id}`),
  inviteInfo: (token: string) => request<InviteInfoDTO>('GET', `/api/invites/${encodeURIComponent(token)}`),
  acceptInvite: (token: string, account?: { name: string; password: string }) =>
    request<{ campaignId: string; user: NonNullable<AuthState['user']> }>('POST', `/api/invites/${encodeURIComponent(token)}/accept`, account),

  noteAccess: (cid: string, nid: string) => request<NoteAccessDTO[]>('GET', `${c(cid)}/notes/${nid}/access`),
  discoveries: (cid: string) => request<DiscoveryDTO[]>('GET', `${c(cid)}/discoveries`),
  markSeen: (cid: string, nid: string) => request<{ ok: true }>('POST', `${c(cid)}/notes/${nid}/seen`),
  markAllSeen: (cid: string) => request<{ ok: true }>('POST', `${c(cid)}/discoveries/seen`),

  users: () => request<UserDTO[]>('GET', '/api/users'),
  updateUser: (id: string, patch: { isAdmin?: boolean; password?: string }) => request<{ ok: true }>('PATCH', `/api/users/${id}`, patch),
  deleteUser: (id: string) => request<{ ok: true }>('DELETE', `/api/users/${id}`),

  campaigns: () => request<CampaignDTO[]>('GET', '/api/campaigns'),
  campaign: (cid: string) => request<CampaignDTO>('GET', c(cid)),
  createCampaign: (name: string, ruleset: string) => request<CampaignDTO>('POST', '/api/campaigns', { name, ruleset }),
  updateCampaign: (cid: string, patch: { name?: string; ruleset?: string }) => request<CampaignDTO>('PATCH', c(cid), patch),
  deleteCampaign: (cid: string) => request<{ ok: true }>('DELETE', c(cid)),
  types: (cid: string) => request<NoteTypeDTO[]>('GET', `${c(cid)}/types`),
  updateType: (cid: string, key: string, patch: { label?: string; color?: string }) =>
    request<NoteTypeDTO>('PATCH', `${c(cid)}/types/${encodeURIComponent(key)}`, patch),

  notes: (cid: string) => request<NoteIndexEntry[]>('GET', `${c(cid)}/notes`),
  note: (cid: string, nid: string) => request<NoteDetail>('GET', `${c(cid)}/notes/${nid}`),
  createNote: (cid: string, input: CreateNoteInput) => request<NoteDetail>('POST', `${c(cid)}/notes`, input),
  updateNote: (cid: string, nid: string, input: UpdateNoteInput) => request<NoteDetail>('PUT', `${c(cid)}/notes/${nid}`, input),
  deleteNote: (cid: string, nid: string) => request<{ ok: true }>('DELETE', `${c(cid)}/notes/${nid}`),

  search: (cid: string, q: string) => request<SearchHit[]>('GET', `${c(cid)}/search?q=${encodeURIComponent(q)}`),
  graph: (cid: string) => request<GraphDTO>('GET', `${c(cid)}/graph`),
  hierarchy: (cid: string) => request<HierarchyDTO>('GET', `${c(cid)}/hierarchy`),
  openLinks: (cid: string) => request<OpenLink[]>('GET', `${c(cid)}/open-links`),

  uploadFile: (cid: string, file: File, folder = 'Bilder') => {
    const fd = new FormData();
    fd.append('folder', folder);
    fd.append('file', file);
    return request<{ id: string; name: string; folder: string; url: string }>('POST', `${c(cid)}/files`, fd);
  },

  importPreview: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return request<ImportPreview>('POST', '/api/import/preview', fd);
  },
  importCommit: (input: {
    token: string;
    campaignId?: string;
    newCampaignName?: string;
    ruleset?: string;
    folders: Record<string, { typeKey: string; visibility: string }>;
  }) => request<ImportReport>('POST', '/api/import/commit', input),

  exportUrl: (cid: string) => `${c(cid)}/export`,
  notesOfType: (cid: string, typeKey: string) => request<NoteWithProps[]>('GET', `${c(cid)}/types/${encodeURIComponent(typeKey)}/notes`),
  patchFrontmatter: (cid: string, nid: string, set: Record<string, unknown>, unset: string[] = []) =>
    request<NoteDetail>('PATCH', `${c(cid)}/notes/${nid}/frontmatter`, { set, unset }),
  screenSettings: (cid: string) => request<ScreenSettingsDTO>('GET', `${c(cid)}/screen`),
  updateScreen: (cid: string, patch: { mapNoteId?: string | null; view?: ViewRect | null }) => request<ScreenSettingsDTO>('PUT', `${c(cid)}/screen`, patch),
  rotateScreen: (cid: string) => request<ScreenSettingsDTO>('POST', `${c(cid)}/screen/rotate`),
  disableScreen: (cid: string) => request<ScreenSettingsDTO>('DELETE', `${c(cid)}/screen`),
  /** Öffentlich (ohne Login) – eigener Fetch, damit kein Login-Umweg greift. */
  screen: async (token: string): Promise<ScreenDTO> => {
    const res = await fetch(`/api/screen/${encodeURIComponent(token)}`, { credentials: 'omit' });
    if (!res.ok) throw new Error(res.status === 404 ? 'not_found' : `HTTP ${res.status}`);
    return res.json() as Promise<ScreenDTO>;
  },
  files: (cid: string) => request<FileEntry[]>('GET', `${c(cid)}/files`),
  map: (cid: string, nid: string) => request<MapDTO>('GET', `${c(cid)}/maps/${nid}`),
  updateMap: (cid: string, nid: string, patch: { grid?: Partial<MapGrid>; fog?: FogState; image?: { width: number; height: number }; templates?: MapTemplate[] }) => request<MapDTO>('PATCH', `${c(cid)}/maps/${nid}`, patch),
  createPin: (cid: string, nid: string, pin: { x: number; y: number; label?: string; noteId?: string | null; visibility?: MapPinDTO['visibility'] }) =>
    request<MapDTO>('POST', `${c(cid)}/maps/${nid}/pins`, pin),
  updatePin: (cid: string, nid: string, pid: string, patch: Partial<Pick<MapPinDTO, 'x' | 'y' | 'label' | 'noteId' | 'visibility' | 'color'>>) =>
    request<MapDTO>('PATCH', `${c(cid)}/maps/${nid}/pins/${pid}`, patch),
  deletePin: (cid: string, nid: string, pid: string) => request<MapDTO>('DELETE', `${c(cid)}/maps/${nid}/pins/${pid}`),
  createToken: (cid: string, nid: string, token: { x: number; y: number; name: string; size?: number; color?: string; noteId?: string | null }) =>
    request<MapDTO>('POST', `${c(cid)}/maps/${nid}/tokens`, token),
  updateToken: (cid: string, nid: string, tid: string, patch: Partial<Pick<MapTokenDTO, 'x' | 'y' | 'name' | 'size' | 'color' | 'hidden' | 'image'>>) =>
    request<MapDTO>('PATCH', `${c(cid)}/maps/${nid}/tokens/${tid}`, patch),
  deleteToken: (cid: string, nid: string, tid: string) => request<MapDTO>('DELETE', `${c(cid)}/maps/${nid}/tokens/${tid}`),
  catalog: (cid: string, kind?: CatalogKind) => request<CatalogItemDTO[]>('GET', `${c(cid)}/catalog${kind ? `?kind=${kind}` : ''}`),
  createCatalogItem: (cid: string, input: { kind: CatalogKind; name: string; value?: number; weight?: number; data: CatalogItemDTO['data'] }) =>
    request<CatalogItemDTO>('POST', `${c(cid)}/catalog`, input),
  updateCatalogItem: (cid: string, id: string, patch: Record<string, unknown>) => request<CatalogItemDTO>('PATCH', `${c(cid)}/catalog/${id}`, patch),
  deleteCatalogItem: (cid: string, id: string) => request<{ ok: true }>('DELETE', `${c(cid)}/catalog/${id}`),
  clearCatalog: (cid: string, kind: CatalogKind) => request<{ removed: number }>('POST', `${c(cid)}/catalog/clear`, { kind }),
  catalogDefaults: (cid: string, kind: CatalogKind) => request<{ added: number }>('POST', `${c(cid)}/catalog/defaults`, { kind }),
  importCatalog: (cid: string, kind: CatalogKind, mode: 'merge' | 'replace', csv: string) =>
    request<CatalogImportResult>('POST', `${c(cid)}/catalog/import`, { kind, mode, csv }),
  items: (cid: string) => request<ItemSuggestion[]>('GET', `${c(cid)}/items`),
  appendLog: (cid: string, nid: string, text: string, time?: string) => request<NoteDetail>('POST', `${c(cid)}/notes/${nid}/log`, { text, time }),

  combats: (cid: string) => request<CombatListEntry[]>('GET', `${c(cid)}/combats`),
  combat: (cid: string, kid: string) => request<CombatDTO>('GET', `${c(cid)}/combats/${kid}`),
  createCombat: (cid: string, input: { name: string; sessionNoteId?: string | null }) =>
    request<CombatDTO>('POST', `${c(cid)}/combats`, input),
  updateCombat: (cid: string, kid: string, patch: { name?: string; sessionNoteId?: string | null; mapNoteId?: string | null; spawn?: { x: number; y: number } }) =>
    request<CombatDTO>('PATCH', `${c(cid)}/combats/${kid}`, patch),
  deleteCombat: (cid: string, kid: string) => request<{ ok: true }>('DELETE', `${c(cid)}/combats/${kid}`),
  addCombatants: (
    cid: string,
    kid: string,
    input: { noteId?: string; name?: string; count?: number; hpMax?: number; ac?: number; initBonus?: number; kind?: CombatantDTO['kind']; spawn?: { x: number; y: number } },
  ) => request<CombatDTO>('POST', `${c(cid)}/combats/${kid}/combatants`, input),
  addAllCharacters: (cid: string, kid: string, spawn?: { x: number; y: number }) => request<CombatDTO>('POST', `${c(cid)}/combats/${kid}/characters`, { spawn }),
  updateCombatant: (cid: string, kid: string, bid: string, patch: Record<string, unknown>) =>
    request<CombatDTO>('PATCH', `${c(cid)}/combats/${kid}/combatants/${bid}`, patch),
  removeCombatant: (cid: string, kid: string, bid: string) => request<CombatDTO>('DELETE', `${c(cid)}/combats/${kid}/combatants/${bid}`),
  changeHp: (cid: string, kid: string, bid: string, kind: 'damage' | 'heal' | 'temp' | 'set', amount: number) =>
    request<CombatActionResult>('POST', `${c(cid)}/combats/${kid}/combatants/${bid}/hp`, { kind, amount }),
  turn: (cid: string, kid: string, direction: 1 | -1) => request<CombatActionResult>('POST', `${c(cid)}/combats/${kid}/turn`, { direction }),
  endCombat: (cid: string, kid: string, writeToSession: boolean) =>
    request<CombatDTO>('POST', `${c(cid)}/combats/${kid}/end`, { writeToSession }),

  // Terminfinder
  polls: (cid: string) => request<PollListEntry[]>('GET', `${c(cid)}/polls`),
  schedule: (cid: string) => request<ScheduleDTO>('GET', `${c(cid)}/schedule`),
  poll: (cid: string, pid: string) => request<PollDTO>('GET', `${c(cid)}/polls/${pid}`),
  createPoll: (cid: string, input: CreatePollInput) => request<PollDTO>('POST', `${c(cid)}/polls`, input),
  updatePoll: (cid: string, pid: string, input: UpdatePollInput) => request<PollDTO>('PATCH', `${c(cid)}/polls/${pid}`, input),
  deletePoll: (cid: string, pid: string) => request<{ ok: true }>('DELETE', `${c(cid)}/polls/${pid}`),
  addPollSlot: (cid: string, pid: string, slot: { startAt: string; endAt: string | null }) =>
    request<PollDTO>('POST', `${c(cid)}/polls/${pid}/slots`, slot),
  deletePollSlot: (cid: string, pid: string, sid: string) => request<PollDTO>('DELETE', `${c(cid)}/polls/${pid}/slots/${sid}`),
  addPollParticipant: (cid: string, pid: string, input: { displayName: string; userId: string | null; isRequired: boolean }) =>
    request<PollDTO>('POST', `${c(cid)}/polls/${pid}/participants`, input),
  updatePollParticipant: (cid: string, pid: string, ppid: string, input: { displayName?: string; isRequired?: boolean }) =>
    request<PollDTO>('PATCH', `${c(cid)}/polls/${pid}/participants/${ppid}`, input),
  deletePollParticipant: (cid: string, pid: string, ppid: string) => request<PollDTO>('DELETE', `${c(cid)}/polls/${pid}/participants/${ppid}`),
  rotatePollLink: (cid: string, pid: string, ppid: string) => request<PollDTO>('POST', `${c(cid)}/polls/${pid}/participants/${ppid}/token`),
  revokePollLink: (cid: string, pid: string, ppid: string) => request<PollDTO>('DELETE', `${c(cid)}/polls/${pid}/participants/${ppid}/token`),
  setPollVote: (cid: string, pid: string, participantId: string, slotId: string, answer: PollAnswer | null) =>
    request<PollDTO>('PUT', `${c(cid)}/polls/${pid}/votes`, { participantId, slotId, answer }),
  finalizePoll: (cid: string, pid: string, slotId: string) => request<PollDTO>('POST', `${c(cid)}/polls/${pid}/finalize`, { slotId }),
  ballot: (cid: string, pid: string) => request<BallotDTO>('GET', `${c(cid)}/polls/${pid}/ballot`),
  castBallot: (cid: string, pid: string, sid: string, answer: PollAnswer, comment: string) =>
    request<BallotDTO>('PUT', `${c(cid)}/polls/${pid}/ballot/${sid}`, { answer, comment }),
  pollIcsUrl: (cid: string, pid: string) => `${c(cid)}/polls/${pid}/session.ics`,
  publicBallot: (token: string) => request<BallotDTO>('GET', `/api/polls/${encodeURIComponent(token)}`),
  castPublicBallot: (token: string, sid: string, answer: PollAnswer, comment: string) =>
    request<BallotDTO>('PUT', `/api/polls/${encodeURIComponent(token)}/votes/${sid}`, { answer, comment }),
  publicIcsUrl: (token: string) => `/api/polls/${encodeURIComponent(token)}/session.ics`,
};
